import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Package,
  Upload,
  Download,
  FileText,
  Truck,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Tag,
  Layers,
  X,
  Files,
  Settings2,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const MAX_FILES = 5;

// All available codes for division toggle
const ALL_DIVISION_CODES = [
  { code: "1021", label: "1021", color: "bg-violet-500" },
  { code: "8000", label: "8000", color: "bg-cyan-500" },
  { code: "M57", label: "M57", color: "bg-emerald-500" },
  { code: "2010", label: "2010", color: "bg-rose-500" },
  { code: "ST10", label: "ST10", color: "bg-amber-500" },
  { code: "1076", label: "1076", color: "bg-pink-500" },
  { code: "C0K", label: "C0K", color: "bg-orange-500" },
  { code: "222", label: "222", color: "bg-blue-500" },
  { code: "570", label: "570", color: "bg-indigo-500" },
  { code: "VF7137", label: "VF7137", color: "bg-purple-500" },
];

// Default active codes
const DEFAULT_ACTIVE_CODES = ["1021", "8000", "M57", "C0K", "2010"];

const Spedizioni = () => {
  // State per divisione per ML
  const [processingML, setProcessingML] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const uploadOpts = {
    timeout: 0,
    onUploadProgress: (e) => e.total && setUploadPct(Math.round((e.loaded / e.total) * 100)),
  };
  const [resultML, setResultML] = useState(null);
  const [dragActiveML, setDragActiveML] = useState(false);
  const [filesML, setFilesML] = useState([]);
  const fileRefML = useRef(null);

  // State per divisione per Codice
  const [processingCode, setProcessingCode] = useState(false);
  const [resultCode, setResultCode] = useState(null);
  const [dragActiveCode, setDragActiveCode] = useState(false);
  const [filesCode, setFilesCode] = useState([]);
  const fileRefCode = useRef(null);
  
  // State per i codici attivi (toggle switches)
  const [activeCodes, setActiveCodes] = useState(() => {
    // Load from localStorage or use defaults
    const saved = localStorage.getItem('spedizioni_active_codes');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_ACTIVE_CODES;
      }
    }
    return DEFAULT_ACTIVE_CODES;
  });
  
  const [showCodeSettings, setShowCodeSettings] = useState(false);

  // UFL Protection system states
  const [uflCode, setUflCode] = useState("");
  const [uflHtmlFisso, setUflHtmlFisso] = useState(false);
  const [uflHtmlVariabile, setUflHtmlVariabile] = useState(true);
  const [uflHtmlDinamico, setUflHtmlDinamico] = useState(false);
  const [showUflPanel, setShowUflPanel] = useState(false);
  const [uflProcessType, setUflProcessType] = useState('code'); // 'ml' or 'code'

  // Validate UFL code (must end with 4)
  const validateUflCode = () => {
    if (!uflCode || !uflCode.trim().endsWith('4')) {
      toast.error("Codice UFL non valido. Verifica e riprova.");
      return false;
    }
    return true;
  };

  // Save active codes to localStorage when they change
  useEffect(() => {
    localStorage.setItem('spedizioni_active_codes', JSON.stringify(activeCodes));
  }, [activeCodes]);

  // Toggle a code on/off
  const toggleCode = (code) => {
    setActiveCodes(prev => {
      if (prev.includes(code)) {
        // Don't allow disabling all codes
        if (prev.length <= 1) {
          toast.error("Devi avere almeno un codice attivo");
          return prev;
        }
        return prev.filter(c => c !== code);
      } else {
        return [...prev, code];
      }
    });
  };

  // Handler per drag ML
  const handleDragML = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveML(true);
    } else if (e.type === "dragleave") {
      setDragActiveML(false);
    }
  };

  const handleDropML = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveML(false);
    if (e.dataTransfer.files) {
      addFilesML(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelectML = (e) => {
    if (e.target.files) {
      addFilesML(Array.from(e.target.files));
    }
  };

  const addFilesML = (newFiles) => {
    const pdfFiles = newFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length !== newFiles.length) {
      toast.error("Solo file PDF sono accettati");
    }
    const totalFiles = [...filesML, ...pdfFiles].slice(0, MAX_FILES);
    if (totalFiles.length < filesML.length + pdfFiles.length) {
      toast.warning(`Massimo ${MAX_FILES} file consentiti`);
    }
    setFilesML(totalFiles);
  };

  const removeFileML = (index) => {
    setFilesML(filesML.filter((_, i) => i !== index));
  };

  // Handler per drag Code
  const handleDragCode = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActiveCode(true);
    } else if (e.type === "dragleave") {
      setDragActiveCode(false);
    }
  };

  const handleDropCode = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveCode(false);
    if (e.dataTransfer.files) {
      addFilesCode(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelectCode = (e) => {
    if (e.target.files) {
      addFilesCode(Array.from(e.target.files));
    }
  };

  const addFilesCode = (newFiles) => {
    const pdfFiles = newFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length !== newFiles.length) {
      toast.error("Solo file PDF sono accettati");
    }
    const totalFiles = [...filesCode, ...pdfFiles].slice(0, MAX_FILES);
    if (totalFiles.length < filesCode.length + pdfFiles.length) {
      toast.warning(`Massimo ${MAX_FILES} file consentiti`);
    }
    setFilesCode(totalFiles);
  };

  const removeFileCode = (index) => {
    setFilesCode(filesCode.filter((_, i) => i !== index));
  };

  // Show UFL panel before processing ML
  const handleProcessMLClick = () => {
    if (filesML.length === 0) {
      toast.error("Seleziona almeno un file PDF");
      return;
    }
    
    // Show UFL authorization panel for ML
    setShowUflPanel(true);
    setUflProcessType('ml');
  };

  // Process files per ML (called after UFL validation)
  const processFilesML = async () => {
    // Validate UFL code
    if (!validateUflCode()) {
      return;
    }
    
    setShowUflPanel(false);
    setProcessingML(true);
    setUploadPct(0);
    setResultML(null);

    try {
      const formData = new FormData();
      // Append each file separately with the same key name "files"
      for (let i = 0; i < filesML.length; i++) {
        formData.append("files", filesML[i]);
      }

      const res = await api.post("/spedizioni/split-pdf-multiple", formData, uploadOpts);
      
      if (res.data && res.data.success) {
        setResultML(res.data);
        toast.success(res.data.message);
        setFilesML([]);
        // Reset UFL code after success
        setUflCode("");
      } else {
        toast.error(res.data?.detail || "Errore nell'elaborazione");
      }
    } catch (error) {
      console.error("PDF split error:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          "Errore nell'elaborazione dei PDF";
      toast.error(errorMessage);
    } finally {
      setProcessingML(false);
      if (fileRefML.current) fileRefML.current.value = "";
    }
  };

  // Show UFL panel before processing Code
  const handleProcessCodeClick = () => {
    if (filesCode.length === 0) {
      toast.error("Seleziona almeno un file PDF");
      return;
    }
    
    if (activeCodes.length === 0) {
      toast.error("Attiva almeno un codice per la divisione");
      return;
    }
    
    // Show UFL authorization panel for Code
    setShowUflPanel(true);
    setUflProcessType('code');
  };

  // Process files per Codice (called after UFL validation)
  const processFilesCode = async () => {
    // Validate UFL code
    if (!validateUflCode()) {
      return;
    }
    
    setShowUflPanel(false);
    setProcessingCode(true);
    setUploadPct(0);
    setResultCode(null);

    try {
      const formData = new FormData();
      // Append each file separately with the same key name "files"
      for (let i = 0; i < filesCode.length; i++) {
        formData.append("files", filesCode[i]);
      }
      // Append active codes as JSON
      formData.append("target_codes", JSON.stringify(activeCodes));

      const res = await api.post("/spedizioni/split-by-code-multiple", formData, uploadOpts);
      
      if (res.data && res.data.success) {
        setResultCode(res.data);
        toast.success(res.data.message);
        setFilesCode([]);
        // Reset UFL code after success
        setUflCode("");
      } else {
        toast.error(res.data?.detail || "Errore nell'elaborazione");
      }
    } catch (error) {
      console.error("PDF split by code error:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          "Errore nell'elaborazione dei PDF";
      toast.error(errorMessage);
    } finally {
      setProcessingCode(false);
      if (fileRefCode.current) fileRefCode.current.value = "";
    }
  };

  const saveBlob = (data, filename, type) => {
    const url = URL.createObjectURL(new Blob([data], { type }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const downloadErr = (e) => toast.error(e.response?.status === 404 ? "File non più disponibile (oltre 24 ore): ripeti la divisione" : "Download non riuscito, riprova");

  const downloadFile = async (fileData) => {
    try {
      const res = await api.get(fileData.url, { responseType: "blob", timeout: 0 });
      saveBlob(res.data, fileData.filename, "application/pdf");
      toast.success(`Download: ${fileData.filename}`);
    } catch (e) {
      downloadErr(e);
    }
  };

  // One single ZIP: phones block several downloads in a row
  const downloadAll = async (result) => {
    if (!result?.zip_url) return;
    const t = toast.loading("Preparo lo ZIP con tutti i PDF...");
    try {
      const res = await api.get(result.zip_url, { responseType: "blob", timeout: 0 });
      saveBlob(res.data, `spedizioni_${result.job_id.slice(0, 6)}.zip`, "application/zip");
      toast.success(`ZIP scaricato: ${Object.keys(result.files).length} PDF`, { id: t });
    } catch (e) {
      toast.dismiss(t);
      downloadErr(e);
    }
  };

  const getCategoryColorML = (category) => {
    const colors = {
      "30ml": "bg-blue-100 text-blue-700 border-blue-200",
      "50ml": "bg-green-100 text-green-700 border-green-200",
      "100ml": "bg-purple-100 text-purple-700 border-purple-200",
      "200ml": "bg-orange-100 text-orange-700 border-orange-200",
      "500ml": "bg-red-100 text-red-700 border-red-200",
      "misti": "bg-gray-100 text-gray-700 border-gray-200",
    };
    return colors[category] || colors["misti"];
  };

  const getCategoryColorCode = (category) => {
    const codeConfig = ALL_DIVISION_CODES.find(c => c.code === category);
    if (codeConfig) {
      const colorMap = {
        "bg-violet-500": "bg-violet-100 text-violet-700 border-violet-200",
        "bg-cyan-500": "bg-cyan-100 text-cyan-700 border-cyan-200",
        "bg-emerald-500": "bg-emerald-100 text-emerald-700 border-emerald-200",
        "bg-rose-500": "bg-rose-100 text-rose-700 border-rose-200",
        "bg-amber-500": "bg-amber-100 text-amber-700 border-amber-200",
        "bg-pink-500": "bg-pink-100 text-pink-700 border-pink-200",
        "bg-orange-500": "bg-orange-100 text-orange-700 border-orange-200",
        "bg-blue-500": "bg-blue-100 text-blue-700 border-blue-200",
        "bg-indigo-500": "bg-indigo-100 text-indigo-700 border-indigo-200",
        "bg-purple-500": "bg-purple-100 text-purple-700 border-purple-200",
      };
      return colorMap[codeConfig.color] || "bg-gray-100 text-gray-700 border-gray-200";
    }
    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  const ML_CATEGORIES = ["30ml", "50ml", "100ml", "200ml", "500ml", "misti"];
  // Dynamic CODE_CATEGORIES based on active codes
  const CODE_CATEGORIES = [...activeCodes, "misti"];

  // File list component
  const FileList = ({ files, onRemove, disabled }) => (
    <div className="mt-4 space-y-2">
      {files.map((file, index) => (
        <div
          key={index}
          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
        >
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-gray-500" />
            <div>
              <p className="text-sm font-medium text-gray-700">{file.name}</p>
              <p className="text-xs text-gray-400">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
          {!disabled && (
            <button
              onClick={() => onRemove(index)}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div data-testid="spedizioni-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">
          Spedizioni TikTok
        </h1>
        <p className="text-gray-500">
          Dividi automaticamente i PDF delle spedizioni (max {MAX_FILES} file)
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="ml" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="ml" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Dividi per ML
          </TabsTrigger>
          <TabsTrigger value="code" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Dividi per Codice
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Dividi per ML */}
        <TabsContent value="ml" className="space-y-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-[#C9A227]/20 to-[#C9A227]/5 rounded-xl">
                  <Truck className="w-6 h-6 text-[#C9A227]" />
                </div>
                <div>
                  <CardTitle className="text-gray-900 text-xl">
                    Dividi PDF per Formato ML
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Carica fino a {MAX_FILES} file PDF TikTok
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Drag & Drop Zone ML */}
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                  dragActiveML
                    ? "border-[#C9A227] bg-amber-50"
                    : "border-gray-300 hover:border-[#C9A227]/50 hover:bg-gray-50"
                }`}
                onDragEnter={handleDragML}
                onDragLeave={handleDragML}
                onDragOver={handleDragML}
                onDrop={handleDropML}
              >
                <input
                  ref={fileRefML}
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFileSelectML}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={processingML || filesML.length >= MAX_FILES}
                />
                
                {processingML ? (
                  <div className="space-y-4">
                    <Loader2 className="w-12 h-12 text-[#C9A227] mx-auto animate-spin" />
                    <div>
                      <p className="text-gray-900 font-medium" data-testid="split-progress-label">{uploadPct < 100 ? `Caricamento ${uploadPct}%` : "Divisione in corso..."}</p>
                      <p className="text-sm text-gray-500">Divisione di {filesML.length} file per formato ML</p>
                    </div>
                    <Progress value={uploadPct < 100 ? uploadPct : 100} className="w-48 mx-auto" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#C9A227]/20 to-[#C9A227]/5 rounded-full flex items-center justify-center mx-auto">
                      <Files className="w-8 h-8 text-[#C9A227]" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">
                        Trascina qui i PDF oppure clicca
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {filesML.length > 0 
                          ? `${filesML.length}/${MAX_FILES} file selezionati`
                          : `Max ${MAX_FILES} file PDF`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* File List ML */}
              {filesML.length > 0 && (
                <FileList files={filesML} onRemove={removeFileML} disabled={processingML} />
              )}

              {/* Process Button ML */}
              {filesML.length > 0 && !processingML && (
                <Button
                  onClick={handleProcessMLClick}
                  className="w-full mt-4 bg-[#C9A227] text-white hover:bg-[#b8922a]"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Elabora {filesML.length} file
                </Button>
              )}

              {/* Info Box ML */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">Divide per:</p>
                    <p className="mt-1">30ml • 50ml • 100ml • 200ml • 500ml • Misti</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results ML */}
          {resultML && (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <CardTitle className="text-gray-900 text-lg">
                        Elaborazione Completata
                      </CardTitle>
                      <p className="text-sm text-gray-500">
                        {resultML.total_orders} ordini da {resultML.files_processed} file divisi
                      </p>
                    </div>
                  </div>
                  {Object.keys(resultML.files || {}).length > 1 && (
                    <Button
                      onClick={() => downloadAll(resultML)}
                      data-testid="download-all-ml-btn"
                      className="bg-[#C9A227] text-white hover:bg-[#b8922a]"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Scarica Tutti (ZIP)
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Stats Grid ML */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                  {ML_CATEGORIES.map((cat) => (
                    <div
                      key={cat}
                      className={`p-4 rounded-xl border ${getCategoryColorML(cat)} text-center`}
                    >
                      <div className="flex items-center justify-center gap-2 mb-2">
                        {cat === "misti" ? <Sparkles className="w-4 h-4" /> : <Package className="w-4 h-4" />}
                        <span className="font-semibold uppercase text-sm">{cat}</span>
                      </div>
                      <p className="text-2xl font-bold">{resultML.categories?.[cat] || 0}</p>
                      <p className="text-xs opacity-75">ordini</p>
                    </div>
                  ))}
                </div>

                {/* Download Buttons ML */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">File Generati:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {resultML.files && Object.entries(resultML.files).map(([cat, fileData]) => (
                      <button
                        key={cat}
                        onClick={() => downloadFile(fileData)}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all hover:shadow-md ${getCategoryColorML(cat)}`}
                      >
                        <FileText className="w-8 h-8" />
                        <div className="text-left flex-1">
                          <p className="font-semibold">{fileData.filename}</p>
                          <p className="text-xs opacity-75">
                            {fileData.num_orders} ordini • {fileData.num_pages} pagine
                          </p>
                        </div>
                        <Download className="w-5 h-5" />
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* TAB 2: Dividi per Codice */}
        <TabsContent value="code" className="space-y-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-xl">
                  <Tag className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-gray-900 text-xl">
                    Dividi PDF per Codice Profumo
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Carica fino a {MAX_FILES} file PDF (solo ordini singoli)
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Drag & Drop Zone Code */}
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                  dragActiveCode
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-300 hover:border-purple-400 hover:bg-gray-50"
                }`}
                onDragEnter={handleDragCode}
                onDragLeave={handleDragCode}
                onDragOver={handleDragCode}
                onDrop={handleDropCode}
              >
                <input
                  ref={fileRefCode}
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFileSelectCode}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={processingCode || filesCode.length >= MAX_FILES}
                />
                
                {processingCode ? (
                  <div className="space-y-4">
                    <Loader2 className="w-12 h-12 text-purple-600 mx-auto animate-spin" />
                    <div>
                      <p className="text-gray-900 font-medium" data-testid="split-progress-label-code">{uploadPct < 100 ? `Caricamento ${uploadPct}%` : "Divisione in corso..."}</p>
                      <p className="text-sm text-gray-500">Divisione di {filesCode.length} file per codice profumo</p>
                    </div>
                    <Progress value={uploadPct < 100 ? uploadPct : 100} className="w-48 mx-auto" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-purple-500/5 rounded-full flex items-center justify-center mx-auto">
                      <Files className="w-8 h-8 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">
                        Trascina qui i PDF oppure clicca
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {filesCode.length > 0 
                          ? `${filesCode.length}/${MAX_FILES} file selezionati`
                          : `Max ${MAX_FILES} file PDF`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* File List Code */}
              {filesCode.length > 0 && (
                <FileList files={filesCode} onRemove={removeFileCode} disabled={processingCode} />
              )}

              {/* Process Button Code */}
              {filesCode.length > 0 && !processingCode && (
                <Button
                  onClick={handleProcessCodeClick}
                  className="w-full mt-4 bg-purple-600 text-white hover:bg-purple-700"
                >
                  <Tag className="w-4 h-4 mr-2" />
                  Elabora {filesCode.length} file
                </Button>
              )}

              {/* Code Toggle Settings */}
              <div className="mt-4 p-4 bg-gradient-to-br from-purple-50 to-slate-50 rounded-xl border border-purple-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Settings2 className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Codici per Divisione</p>
                      <p className="text-xs text-gray-500">{activeCodes.length} codici attivi</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCodeSettings(!showCodeSettings)}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-100"
                  >
                    {showCodeSettings ? "Chiudi" : "Configura"}
                  </Button>
                </div>
                
                {/* Active codes preview */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {activeCodes.map(code => {
                    const codeConfig = ALL_DIVISION_CODES.find(c => c.code === code);
                    return (
                      <span 
                        key={code}
                        className={`px-3 py-1.5 ${codeConfig?.color || 'bg-gray-500'} text-white rounded-full font-mono text-xs font-bold shadow-sm`}
                      >
                        {code}
                      </span>
                    );
                  })}
                  <span className="px-3 py-1.5 bg-gray-400 text-white rounded-full font-mono text-xs font-bold shadow-sm">
                    Misti
                  </span>
                </div>
                
                {/* Toggle switches panel */}
                {showCodeSettings && (
                  <div className="mt-4 pt-4 border-t border-purple-200">
                    <p className="text-xs text-gray-500 mb-3">Attiva/disattiva i codici per la divisione:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {ALL_DIVISION_CODES.map(({ code, label, color }) => {
                        const isActive = activeCodes.includes(code);
                        return (
                          <div 
                            key={code}
                            className={`flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                              isActive 
                                ? 'bg-white border-purple-300 shadow-sm' 
                                : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-3 h-3 rounded-full ${color} ${!isActive && 'opacity-30'}`} />
                              <span className={`font-mono font-bold text-sm ${isActive ? 'text-gray-900' : 'text-gray-400'}`}>
                                {label}
                              </span>
                            </div>
                            <Switch
                              checked={isActive}
                              onCheckedChange={() => toggleCode(code)}
                              className="data-[state=checked]:bg-purple-600"
                            />
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-3 text-center">
                      💡 Gli ordini con codici non attivi o con più profumi diversi andranno in "Misti"
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Results Code */}
          {resultCode && (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <CardTitle className="text-gray-900 text-lg">
                        Elaborazione Completata
                      </CardTitle>
                      <p className="text-sm text-gray-500">
                        {resultCode.total_orders} ordini da {resultCode.files_processed} file divisi per codice
                      </p>
                    </div>
                  </div>
                  {Object.keys(resultCode.files || {}).length > 1 && (
                    <Button
                      onClick={() => downloadAll(resultCode)}
                      data-testid="download-all-code-btn"
                      className="bg-purple-600 text-white hover:bg-purple-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Scarica Tutti (ZIP)
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Stats Grid Code */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                  {CODE_CATEGORIES.map((cat) => (
                    <div
                      key={cat}
                      className={`p-4 rounded-xl border ${getCategoryColorCode(cat)} text-center`}
                    >
                      <div className="flex items-center justify-center gap-2 mb-2">
                        {cat === "misti" ? <Sparkles className="w-4 h-4" /> : <Tag className="w-4 h-4" />}
                        <span className="font-semibold uppercase text-sm">{cat}</span>
                      </div>
                      <p className="text-2xl font-bold">{resultCode.categories?.[cat] || 0}</p>
                      <p className="text-xs opacity-75">ordini</p>
                    </div>
                  ))}
                </div>

                {/* Download Buttons Code */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">File Generati:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {resultCode.files && Object.entries(resultCode.files).map(([cat, fileData]) => (
                      <button
                        key={cat}
                        onClick={() => downloadFile(fileData)}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all hover:shadow-md ${getCategoryColorCode(cat)}`}
                      >
                        <FileText className="w-8 h-8" />
                        <div className="text-left flex-1">
                          <p className="font-semibold">{fileData.filename}</p>
                          <p className="text-xs opacity-75">
                            {fileData.num_orders} ordini • {fileData.num_pages} pagine
                          </p>
                        </div>
                        <Download className="w-5 h-5" />
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* UFL Authorization Modal */}
      {showUflPanel && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold">Autorizzazione Richiesta</h3>
                    <p className="text-slate-400 text-xs">Sistema di validazione UFL</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowUflPanel(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-5">
              {/* UFL Code Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Inserisci codice UFL
                </label>
                <input
                  type="text"
                  value={uflCode}
                  onChange={(e) => setUflCode(e.target.value.toUpperCase())}
                  placeholder="Es: ABC123X4"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all font-mono text-lg tracking-wider"
                />
              </div>

              {/* HTML Options Toggles */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Configurazione Output
                </label>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm text-gray-700">File html fisso</span>
                  <Switch
                    checked={uflHtmlFisso}
                    onCheckedChange={setUflHtmlFisso}
                    className="data-[state=checked]:bg-slate-600"
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm text-gray-700">File html variabile</span>
                  <Switch
                    checked={uflHtmlVariabile}
                    onCheckedChange={setUflHtmlVariabile}
                    className="data-[state=checked]:bg-slate-600"
                  />
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm text-gray-700">File html dinamico</span>
                  <Switch
                    checked={uflHtmlDinamico}
                    onCheckedChange={setUflHtmlDinamico}
                    className="data-[state=checked]:bg-slate-600"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={() => uflProcessType === 'ml' ? processFilesML() : processFilesCode()}
                disabled={!uflCode.trim()}
                className="w-full py-6 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-lg rounded-xl shadow-lg"
              >
                ELABORA
              </Button>
              
              <p className="text-xs text-center text-gray-400">
                La validazione UFL è richiesta per motivi di sicurezza
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Spedizioni;
